import React, { useState, useEffect } from 'react';
import { ShieldCheck, Eye, Pencil, X } from 'lucide-react';

const GATE_KEY = 'redaction_review_gate';

// Gate is ON by default; skippable. Returns false only when explicitly disabled.
export const isReviewGateEnabled = () => {
  try { return localStorage.getItem(GATE_KEY) !== 'off'; } catch (e) { return true; }
};
export const setReviewGateEnabled = (on) => {
  try { localStorage.setItem(GATE_KEY, on ? 'on' : 'off'); } catch (e) { /* no-op */ }
};

/**
 * Show-and-tell redaction review gate. Displays the client-side-redacted text
 * BEFORE it is sent to the AI, lets the user edit it, then sends on confirm.
 * 100% in-browser — nothing leaves the device until "Confirm & Send".
 */
export const RedactionReviewModal = ({ open, redactedText, removedItems = [], onConfirm, onCancel }) => {
  const [text, setText] = useState(redactedText || '');
  const [dontAsk, setDontAsk] = useState(false);

  useEffect(() => { setText(redactedText || ''); setDontAsk(false); }, [redactedText, open]);

  if (!open) return null;

  // Summarise removed items by type (no raw PII counts shown beyond the redacted preview itself)
  const byType = removedItems.reduce((acc, r) => {
    const t = r.type || 'PII';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});
  const total = removedItems.length;

  const handleConfirm = () => {
    if (dontAsk) setReviewGateEnabled(false);
    onConfirm(text);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="redaction-review-modal"
    >
      <div className="w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-800 bg-slate-800/40">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-base leading-tight">Review before sending</h3>
              <p className="text-xs text-slate-400">This runs in your browser — nothing is sent until you confirm.</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-slate-500 hover:text-slate-300 transition-colors"
            data-testid="redaction-review-close"
            aria-label="Cancel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Eye className="w-4 h-4 text-emerald-400" />
            <span>
              We removed <strong className="text-emerald-400" data-testid="redaction-removed-count">{total}</strong>{' '}
              item{total !== 1 ? 's' : ''} before this goes to the AI.
            </span>
          </div>

          {total > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="redaction-removed-chips">
              {Object.entries(byType).map(([type, count]) => (
                <span
                  key={type}
                  className="text-xs px-2 py-1 rounded-md bg-slate-800 border border-slate-700 text-slate-300"
                >
                  {type} × {count}
                </span>
              ))}
            </div>
          )}

          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-400 mb-1.5">
              <Pencil className="w-3.5 h-3.5" />
              Redacted message — edit directly if needed
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={7}
              className="w-full px-3 py-2.5 bg-slate-950/60 border border-slate-700 rounded-lg text-white text-sm font-mono leading-relaxed resize-y focus:outline-none focus:border-emerald-600"
              data-testid="redaction-review-textarea"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={dontAsk}
              onChange={(e) => setDontAsk(e.target.checked)}
              data-testid="redaction-review-dontask"
            />
            Don't show this review again (you can re-enable it in Privacy Guard)
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800 bg-slate-800/30">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
            data-testid="redaction-review-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors flex items-center gap-2"
            data-testid="redaction-review-confirm"
          >
            <ShieldCheck className="w-4 h-4" />
            Confirm &amp; Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default RedactionReviewModal;
